import React, { useState, useEffect, useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle';
import { Mail, CheckCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { authApi } from '../api/auth';

export default function VerifyEmailPage() {
  usePageTitle("Vérification de votre adresse email — Lodiss");
  const { lang, setPage, verifyEmailAndLogin } = useApp();
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(c => c - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await verifyEmailAndLogin(code);
      setSuccess(true);
      
      setTimeout(() => {
        setPage('catalogue');
      }, 1500);
    } catch (err: any) {
      const errorMessage = err.message?.toLowerCase() || '';
      const errorDetails = err.details || '';
      
      if (errorMessage.includes('already verified') || errorDetails.includes('already verified')) {
        setSuccess(true);
        setTimeout(() => {
          setPage('catalogue');
        }, 500);
        return;
      }
      
      if (errorMessage.includes('expired') || errorDetails.includes('expired')) {
        setError(lang === 'fr'
          ? 'Ce code a expiré. Veuillez en demander un nouveau.'
          : 'This code has expired. Please request a new one.');
        if (resendButtonRef.current) {
          resendButtonRef.current.focus();
        }
        setResendCooldown(60);
      } else {
        setError(lang === 'fr'
          ? 'Code incorrect. Veuillez vérifier et réessayer.'
          : 'Incorrect code. Please check and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    setError('');
    try {
      await authApi.resendVerification();
      setResendCooldown(60);
    } catch (err: any) {
      const errorMessage = err.message?.toLowerCase() || '';
      
      if (errorMessage.includes('rate limit') || errorMessage.includes('too many') || errorMessage.includes('frequent')) {
        const extendedCooldown = 300;
        setResendCooldown(extendedCooldown);
        setError(lang === 'fr'
          ? 'Vous avez demandé trop de codes récemment. Attendez quelques minutes.'
          : 'You have requested too many codes recently. Wait a few minutes.');
      } else {
        setError(err.message || (lang === 'fr' ? 'Échec de l\'envoi du code.' : 'Failed to resend code.'));
      }
    } finally {
      setResending(false);
    }
  };

  if (success) {
    return (
      <main className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle className="w-16 h-16 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-gray-950 mb-2">
              {lang === 'fr' ? 'Email vérifié !' : 'Email verified!'}
            </h1>
            <p className="text-gray-500 mb-4">
              {lang === 'fr' 
                ? 'Votre email a été vérifié avec succès.' 
                : 'Your email has been verified successfully.'}
            </p>
            <p className="text-sm text-gray-400">
              {lang === 'fr' ? 'Redirection...' : 'Redirecting...'}
            </p>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl border border-gray-100 p-8 shadow-sm">
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center">
                <Mail className="w-8 h-8 text-lodiss-red" />
              </div>
            </div>
            <h1 className="text-2xl font-bold text-gray-950">
              {lang === 'fr' ? 'Vérifiez votre email' : 'Verify your email'}
            </h1>
            <p className="text-sm text-gray-500 mt-2">
              {lang === 'fr'
                ? 'Nous avons envoyé un code de vérification à votre adresse email.'
                : 'We have sent a verification code to your email address.'}
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
              <AlertCircle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <input
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123456"
                className="w-full px-4 py-3 text-center text-xl tracking-widest border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-lodiss-red/20 focus:border-lodiss-red"
                maxLength={6}
                required
              />
              <p className="text-center text-xs text-gray-400 mt-2">
                {lang === 'fr' ? 'Entrez le code à 6 chiffres' : 'Enter the 6-digit code'}
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-lodiss-red hover:bg-lodiss-dark text-white font-semibold rounded-lg transition-colors duration-150 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  {lang === 'fr' ? 'Vérifier' : 'Verify'}
                </>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-gray-100 text-center">
            {resendCooldown > 0 ? (
              <p className="text-sm text-gray-400">
                {lang === 'fr'
                  ? `Réenvoyer dans ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, '0')}s`
                  : `Resend in ${Math.floor(resendCooldown / 60)}:${(resendCooldown % 60).toString().padStart(2, '0')}s`}
              </p>
            ) : (
              <button
                ref={resendButtonRef}
                type="button"
                onClick={handleResend}
                disabled={resending}
                className="flex items-center justify-center gap-2 mx-auto text-sm text-lodiss-red hover:text-lodiss-dark font-medium disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-lodiss-red/20 rounded"
              >
                <RefreshCw size={14} className={resending ? 'animate-spin' : ''} />
                {lang === 'fr' ? 'Renvoyer le code' : 'Resend code'}
              </button>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
